import React from "react";
import {
  Box,
  Card,
  CardActions,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { GitHub, Language } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import TranslatedProjectData from "../data/TranslatedProjectData.json";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import SectionHeading from "../components/SectionHeading";

function ProjectsContainer() {
  const { t, i18n } = useTranslation();
  const { ref, visible } = useScrollAnimation();
  const currentLang = i18n.language || "en";
  const ProjectData =
    TranslatedProjectData[currentLang] || TranslatedProjectData.en;

  return (
    <Box
      id="projects"
      ref={ref}
      sx={{
        py: { xs: 4, md: 8 },
        px: { xs: 1, sm: 2 },
        scrollMarginTop: { xs: "56px", md: "64px" },
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(40px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
    >
      <SectionHeading tag="projects" title={t("projects.title")} number={3} />

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {ProjectData.map((project, projectKey) => (
          <Grid size={{ xs: 12, md: 6 }} key={projectKey}>
            <Card
              sx={{
                height: "100%",
                display: "grid",
                gridTemplateRows: "auto 1fr auto auto",
                background:
                  "linear-gradient(135deg, rgba(17,17,23,0.97) 0%, rgba(9,9,11,0.97) 100%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(99,102,241,0.15)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background:
                    "linear-gradient(90deg, #6366f1, #22d3ee)",
                },
              }}
            >
              {/* Title */}
              <Box
                sx={{
                  pt: { xs: 2.5, md: 3 },
                  px: { xs: 2, md: 3 },
                  pb: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 0.75,
                }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1rem", sm: "1.15rem" },
                    lineHeight: 1.3,
                    color: "text.primary",
                  }}
                >
                  {project.name}
                </Typography>
                {project.isNew && (
                  <Chip
                    label="NEW"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      height: 20,
                      bgcolor: "rgba(34,211,238,0.15)",
                      color: "secondary.main",
                      border: "1px solid rgba(34,211,238,0.3)",
                    }}
                  />
                )}
              </Box>

              {/* Description */}
              <Box sx={{ px: { xs: 2, md: 3 } }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.83rem", sm: "0.875rem" },
                  }}
                >
                  {project.description}
                </Typography>
              </Box>

              {/* Tags */}
              <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {project.tags.slice(0, 5).map((tag, tagIndex) => (
                    <Chip
                      key={tagIndex}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: "rgba(99,102,241,0.12)",
                        color: "primary.light",
                        border: "1px solid rgba(99,102,241,0.2)",
                        fontSize: "0.72rem",
                        height: 22,
                      }}
                    />
                  ))}
                  {project.tags.length > 5 && (
                    <Chip
                      label={`+${project.tags.length - 5}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(34,211,238,0.1)",
                        color: "secondary.light",
                        border: "1px solid rgba(34,211,238,0.2)",
                        fontSize: "0.72rem",
                        height: 22,
                      }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Links */}
              <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <CardActions sx={{ p: 2, gap: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<GitHub />}
                    href={project.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "primary.light",
                      fontSize: "0.8rem",
                      "&:hover": { bgcolor: "rgba(99,102,241,0.1)" },
                    }}
                  >
                    {t("projects.viewSource")}
                  </Button>
                  {project.website && project.website !== project.source && (
                    <Button
                      size="small"
                      startIcon={<Language />}
                      href={project.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "secondary.main",
                        fontSize: "0.8rem",
                        "&:hover": { bgcolor: "rgba(34,211,238,0.08)" },
                      }}
                    >
                      {t("projects.viewWebsite")}
                    </Button>
                  )}
                </CardActions>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default ProjectsContainer;
